// src/api.js
import axios from 'axios';
import qs from 'qs';
import { createAppTransport } from '@1823-partners/core';
import { normalizeCurveScenarioCatalog } from '../utils/curveScenarioUtils';

const api = createAppTransport({ axios, appName: 'trade' });

const qsSerializer = (p) => qs.stringify(p, { arrayFormat: 'repeat' });

const mappedCusips = (trancheMap, tranche) => {
  const mapped = trancheMap?.[tranche];
  return (Array.isArray(mapped) ? mapped : [mapped])
    .map(value => String(value || '').trim())
    .filter(Boolean);
};

const resolveGenerationTrancheMap = async (
  dealData,
  trancheTarget,
  modifiedDate,
  suppliedMap = dealData?.tranche_map || {}
) => {
  const candidateMap = suppliedMap || {};
  const selectedTranches = trancheTarget && typeof trancheTarget === 'object'
    ? Object.keys(trancheTarget)
    : [];

  // A repeated fallback CUSIP makes the backend dataframe index non-unique,
  // so ParseScenarioInputs receives Series values instead of scalar cells.
  const suppliedCusips = selectedTranches.flatMap(
    tranche => mappedCusips(candidateMap, tranche)
  );
  const hasCompleteDistinctMap = selectedTranches.every(
    tranche => mappedCusips(candidateMap, tranche).length > 0
  ) && new Set(suppliedCusips).size === suppliedCusips.length;

  if (selectedTranches.length <= 1 || hasCompleteDistinctMap) {
    return Object.keys(candidateMap).length > 0 ? candidateMap : null;
  }

  const dealName = dealData?.['Deal Name'] || dealData?.DEAL_NAME || dealData?.name;
  if (!dealName) return null;

  const params = { dealName };
  const intexDeal = dealData?.['Intex Deal'] || dealData?.INTEX_DEAL || dealData?.intexDeal;
  if (intexDeal) params.intexDeal = intexDeal;
  if (modifiedDate) params.modifiedDate = modifiedDate;

  const response = await api.get('/deal', { params });
  const authoritativeMap = response.data?.tranche_map || {};
  const missing = selectedTranches.filter(
    tranche => mappedCusips(authoritativeMap, tranche).length === 0
  );
  if (missing.length > 0) {
    throw new Error(`No INTEX CUSIP mapping returned for tranche(s): ${missing.join(', ')}`);
  }

  return Object.fromEntries(
    selectedTranches.map(tranche => [tranche, authoritativeMap[tranche]])
  );
};

export const fetchConfigs = async () => {
  return api.get('/configs');
};

export const fetchDeals = async (params) => {
  return api.get('/deals', {
    params,
    paramsSerializer: qsSerializer,
  });
};

// Save deal with cashflow and analytics data (single endpoint)
export const saveDeal = async (
  dealData,
  cashflowData,
  analyticsData,
  parsedData,
  user,
  adcoData = null,
  tranches = [],
  ownPcts = {},
  purchasePrices = {}
) => {
  return api.post('/save', {
    deal: dealData,
    user: user,
    cashflow: cashflowData,
    analytics: analyticsData,
    parsed: parsedData,
    adco: adcoData,
    tranches: tranches,
    _ownPcts: ownPcts,
    _purchasePrices: purchasePrices,
  });
};

// Most recent blotter buy price per tranche, used to default the purchase
// price in the pricing popup. Returns { tranche: price }.
export const fetchPurchasePrices = async (dealName, tranches, modifiedDate) => {
  return api.get('/deal/purchase-prices', {
    params: { dealName, tranches, modifiedDate },
    paramsSerializer: qsSerializer,
  });
};

export const priceScenario = async (payload) => {
  return api.post('/price', payload);
};

// New deal-specific API functions
export const fetchDealCashflow = async (dealName, tranches, modifiedDate, grain = 'year') => {
  // Always fetch all tranches - pass empty array to get all, filtering will be done in the frontend
  return api.get('/deal/cashflow', {
    params: { dealName, tranches: tranches, modifiedDate, grain },
    paramsSerializer: qsSerializer,
  });
};

export const fetchDealAnalytics = async (dealName, tranches, modifiedDate, asofdt = null) => {
  // Always fetch all tranches - pass empty array to get all, filtering will be done in the frontend
  const params = { dealName, tranches: tranches, modifiedDate };
  if (asofdt) {
    params.asofdt = asofdt;
  }
  return api.get('/deal/analytics', {
    params,
    paramsSerializer: qsSerializer,
  });
};

// Generate cashflow, analytics, and parsed data in one call
// Expected response: { cashflow: [...], analytics: [...], parsed: [...] }
export const generateCashflowAnalytics = async (
  dealData,
  tranche_target,
  modifiedDate,
  adcoData = null,
  asofdt = null
) => {
  // Filter out fields that shouldn't be sent in the payload
  const { tranche_map, security_owner, Load, Edit, Clone, ...filteredDealData } = dealData;

  const payload = {
    deal: filteredDealData,
    tranche_target,
    modifiedDate,
    adco: adcoData,
  };

  const resolvedTrancheMap = await resolveGenerationTrancheMap(
    dealData,
    tranche_target,
    modifiedDate,
    tranche_map
  );
  if (resolvedTrancheMap) {
    payload.tranche_map = resolvedTrancheMap;
  }

  if (asofdt) {
    payload.asofdt = asofdt;
  }

  return api.post('/deal/generate', payload);
};

// A comparison takes 45-90s of INTEX work — longer than the Azure Function that
// fronts the Beacon server allows one request to live, so the server-side result
// used to be discarded by the gateway (browser saw a 500 while the server logged
// a 200). Start the run, poll for it, and hand callers back the same
// `{ data }` shape a plain api.post would have resolved to.
//
// Falls back to the synchronous endpoint when /start isn't there yet, so the
// frontend can deploy before or after the server.
export const COMPARISON_POLL_MS = 2000;
export const COMPARISON_FIRST_POLL_MS = 400;
export const COMPARISON_POLL_TIMEOUT_MS = 10 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Poll a started run until it finishes, resolving to the `{ data }` shape a plain
// api.post would have. Shared by the Intex and Loans comparisons.
const waitForRun = async (started, statusPath, options = {}) => {
  const pollMs = options.pollMs ?? COMPARISON_POLL_MS;
  const timeoutMs = options.timeoutMs ?? COMPARISON_POLL_TIMEOUT_MS;
  // A short first poll so a fast comparison isn't held back by the cadence,
  // then settle into it.
  let delay = options.firstPollMs ?? COMPARISON_FIRST_POLL_MS;
  const onProgress = options.onProgress;
  const runId = started?.data?.run_id;
  if (!runId) {
    throw new Error('Comparison did not start: no run id returned');
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await sleep(delay);
    delay = pollMs;
    const poll = await api.get(`${statusPath}/${runId}`);
    const { status, error, done, total, ...result } = poll.data || {};
    if (onProgress && total) {
      // a finished run is complete by definition, whatever the last count said
      onProgress(status === 'done' ? total : done || 0, total);
    }
    if (status === 'done') {
      return { ...poll, data: result };
    }
    if (status === 'error') {
      throw new Error(error || 'Comparison failed');
    }
  }
  throw new Error(`Comparison timed out after ${Math.round(timeoutMs / 60000)} minutes`);
};

export const postComparison = async (payload, options = {}) => {
  let started;
  try {
    started = await api.post('/deal/generate-comparison/start', payload);
  } catch (err) {
    if (err?.response?.status === 404) {
      return api.post('/deal/generate-comparison', payload);
    }
    throw err;
  }
  return waitForRun(started, '/deal/comparison', options);
};

// --- Loans tab: whole-loan ADCO comparisons ---------------------------------
// Open positions on the latest mortgage tape on or before asofdt (default: today).
export const fetchLoanPositions = async (asofdt = null) => (
  api.get('/loans', { params: asofdt ? { asofdt } : {} })
);

// Validate a parsed tape against the mortgage_runner mapper; nothing is stored.
export const validateLoanTape = async (asofdt, rows) => (
  api.post('/loans/upload', { asofdt, rows })
);

// Loan tapes saved to MORTGAGE.CUSTOM_LOAN_TAPE: list, load (validated again), save.
export const fetchLoanTapes = async () => api.get('/loans/tapes');

// The position tape's month ends, newest first.
export const fetchLoanTapeDates = async () => api.get('/loans/dates');

export const fetchLoanTape = async (name, asofdt) => (
  api.get('/loans/tape', { params: { name, asofdt } })
);

export const saveLoanTape = async (name, asofdt, rows) => (
  api.post('/loans/tapes', { name, asofdt, rows })
);

// payload: { asofdt, loan_ids, tape?, runs: [{ label, curve_scenario?, curve_asofdt?, shock_bps?, target? }] }
export const runLoanComparison = async (payload, options = {}) => {
  const started = await api.post('/loans/comparison/start', payload);
  return waitForRun(started, '/loans/comparison', options);
};

// Generate comparison data (shock or parameter mode)
// comparisonConfig: { comparison_mode: 'shock'|'parameter', shock_bps: number, set_a: {...}, set_b: {...} }
export const generateComparison = async (
  dealData,
  tranche_target,
  modifiedDate,
  comparisonConfig,
  adcoData = null,
  asofdt = null,
  options = {}
) => {
  const { tranche_map, security_owner, Load, Edit, Clone, ...filteredDealData } = dealData;

  const payload = {
    deal: filteredDealData,
    tranche_target,
    modifiedDate,
    adco: adcoData,
    ...comparisonConfig,
  };

  const resolvedTrancheMap = await resolveGenerationTrancheMap(
    dealData,
    tranche_target,
    modifiedDate,
    tranche_map
  );
  if (resolvedTrancheMap) {
    payload.tranche_map = resolvedTrancheMap;
  }

  if (asofdt) {
    payload.asofdt = asofdt;
  }

  return postComparison(payload, options);
};

// Generate multi-parameter comparison (N runs)
export const generateMultiComparison = async (
  dealData,
  runs,
  modifiedDate,
  adcoData = null,
  asofdt = null,
  trancheMap = null,
  options = {}
) => {
  const { tranche_map, security_owner, Load, Edit, Clone, ...filteredDealData } = dealData;
  const payload = {
    deal: filteredDealData,
    comparison_mode: 'multi_parameter',
    runs,
    modifiedDate,
  };
  if (adcoData) {
    payload.adco = adcoData;
  }
  if (asofdt) {
    payload.asofdt = asofdt;
  }
  if (trancheMap) {
    payload.tranche_map = trancheMap;
  }
  return postComparison(payload, options);
};

export const fetchCurveScenarios = async (asofdt) => {
  const response = await api.get('/deal/curve-scenarios', {
    params: { asofdt },
  });
  return {
    ...response,
    data: normalizeCurveScenarioCatalog(response.data),
  };
};

export const checkAdcoEnabled = async (
  dealName,
  assetType,
  tranches,
  modifiedDate,
  intexDeal = null
) => {
  const params = { dealName, assetType, tranches, modifiedDate };
  if (intexDeal) {
    params.intexDeal = intexDeal;
  }
  return api.get('/deal/adco-enabled', {
    params,
    paramsSerializer: qsSerializer,
  });
};

export const fetchEquityTranches = async (dealName, tranches, modifiedDate) => {
  return api.get('/deal/equity-tranches', {
    params: { dealName, tranches, modifiedDate },
    paramsSerializer: qsSerializer,
  });
};

export const fetchDealAdco = async (dealName, assetType, tranches, modifiedDate) => {
  return api.get('/deal/adco', {
    params: { dealName, assetType, tranches, modifiedDate },
    paramsSerializer: qsSerializer,
  });
};

export const runDealAdco = async (
  dealName,
  assetType,
  tranches,
  modifiedDate,
  curveScenario = null,
  curveAsofdt = null,
  trancheMap = null,
  asofdt = null
) => {
  const payload = {
    dealName,
    assetType,
    tranches,
    modifiedDate,
  };
  if (curveScenario !== null) payload.curve_scenario = curveScenario;
  if (curveAsofdt !== null) payload.curve_asofdt = curveAsofdt;
  if (trancheMap !== null) payload.tranche_map = trancheMap;
  if (asofdt !== null) payload.asofdt = asofdt;
  return api.post('/deal/adco/run', payload);
};

export const fetchDealPrice = async (
  dealData,
  tranche_target,
  mode,
  modifiedDate,
  adcoData = null,
  asofdt = null
) => {
  // Filter out fields that shouldn't be sent in the payload
  const { tranche_map, security_owner, Load, Edit, Clone, ...filteredDealData } = dealData;

  const payload = {
    deal: filteredDealData,
    tranche_target,
    mode,
    modifiedDate,
    adco: adcoData,
  };

  if (asofdt) {
    payload.asofdt = asofdt;
  }

  return api.post('/deal/price', payload);
};

// New API functions for form handling
// Field definitions are static per server deploy and needed by two components
// (AddSecurityModal and ComparisonMode), so share one request for the life of
// the page. Failures are not cached — the next caller retries.
let securityFormFieldsPromise = null;

export const fetchSecurityFormFields = () => {
  if (!securityFormFieldsPromise) {
    securityFormFieldsPromise = api.get('/form/security/fields').catch(error => {
      securityFormFieldsPromise = null;
      throw error;
    });
  }
  return securityFormFieldsPromise;
};

// Test hook: clear the module-level fields cache between tests.
export const resetSecurityFormFieldsCache = () => {
  securityFormFieldsPromise = null;
};

export const fetchCustomRamps = async () => {
  return api.get('/form/security/custom-ramps');
};

export const fetchOptionRedemptionValues = async () => {
  return api.get('/form/security/option-redemption-values');
};

export const validateSecurityForm = async (formData) => {
  return api.post('/security/validate', formData);
};

export const loadDeal = async (dealName, password = null, intexDeal = null, modifiedDate = null) => {
  const params = { dealName };
  if (password) {
    params.password = password;
  }
  if (intexDeal) {
    params.intexDeal = intexDeal;
  }
  if (modifiedDate) {
    params.modifiedDate = modifiedDate;
  }
  return api.get('/deal', {
    params,
  });
};

// File upload functions
export const uploadCdi = async (file, user, title) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('user', user);
  formData.append('title', title);

  return api.post('/upload/cdi', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const uploadCashflow = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post('/upload/cashflow', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const uploadRamp = async (file, user) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('user', user);

  return api.post('/upload/ramp', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const fetchBlotter = async (cusips, modifiedDate) => {
  return api.get('/blotter', {
    params: { cusips, modifiedDate },
    paramsSerializer: qsSerializer,
  });
};

export default api;
